/**
 * Build the property bag of the `booting_time` telemetry event.
 *
 * The app finishes starting up in two different places, and each one reports a
 * `booting_time` event: the cold boot in `AppComponent.markAppLoadingAsync`, and the
 * warm resume in `AppComponent.postLoginWarmResumeAsync`. What they report is the
 * same set of properties; only a few of the VALUES differ.
 *
 * Before this module existed, both places wrote that property bag out in full, so the
 * two were copies of each other. Adding a property meant remembering to add it twice,
 * and nothing structural could check that both had been done. The repo made up for
 * that with three tests that read `app.component.ts` as a string and counted matches
 * inside a fixed number of characters — tests that had to be widened whenever somebody
 * added a comment, and that failed in a file nobody was editing when they were not.
 * The deferred finding those tests came from is `df-d1448682999e`.
 *
 * So the payload is built here instead, once, and both places call this function. The
 * tests that used to count characters now call it and look at what it returns.
 *
 * This module deliberately holds no Angular import, so it can be called under plain
 * node — that is the whole reason the old tests had to read source text: `AppComponent`
 * is Angular-bound and cannot be constructed there. Anything that comes from a service
 * or from the browser is passed IN rather than reached for, for the same reason. The
 * one exception is `bootTimings`, which is a plain module singleton a test can drive
 * directly.
 */

import { DateTime } from 'luxon';

import { safeUrlForLog } from 'app/safe-url-for-log';
import { classifyPriorSessionHint, CouchSessionHint } from 'services/couch-session-hint';

import { BootEmission, bootTimings } from './boot-timings';

/**
 * What the caller has to supply, which is everything the two boot paths do not share.
 */
export interface BootingTimeInputs {
  /** how long the boot window took, in milliseconds — `bootTimings.elapsedSinceWindowStartMs()`. */
  bootingTimeMs: number;
  /** which of the several things this event can describe it IS (#4615). */
  bootKind: BootEmission;
  /** whether this was a warm resume rather than a boot from cold. */
  warmResume: boolean;
  /**
   * who is signed in as the boot ends, used to decide whether the stored session hint
   * could have served THIS boot. The cold path reads it from the auth service and may
   * have nobody (a logged-out boot is a normal boot completion); the warm path has the
   * cached user in hand.
   */
  uid: string | null | undefined;
  /**
   * the session hint as it was sampled at boot START, so this boot's own login cannot
   * contaminate the answer. See design-docs/telemetry.md.
   */
  bootStartSessionHint: CouchSessionHint | null;
  /** `SettingsService.getSettingsForTelemetry()`. */
  settingsForTelemetry: { [key: string]: any };
  /**
   * the page address, passed RAW. This function trims it — that is deliberate, so that
   * neither caller can forget to (df-3d45df0969b9). The community-login return trip
   * boots us on our own page with a working forum login in the address bar.
   */
  pageUrl: string;
  /**
   * whether the nav-restore watchdog had to rescue this boot's page restore before the boot could
   * finish (df-d80a99934799). Only the cold path can be rescued before it reports — the warm path
   * reports before its restore runs — so leaving it out means false.
   */
  navRestoreRecovered?: boolean;
  /** the current time, injectable so a test can pin the clock. */
  now?: DateTime;
}

/**
 * Every property name this event carries that is written out here.
 *
 * The per-phase timings (`phase_<name>_ms`) are NOT in this list: they are built at
 * runtime by the boot-timings collector, so which of them appear depends on which
 * phases that boot actually ran.
 *
 * It is exported so a test can pin it. That matters more than it looks:
 * `scripts/ci/check-telemetry-lexicon.mjs` reads property names out of object literals
 * at call sites, and it cannot read a bag built by a function — so once this function
 * existed, the gate stopped seeing these names. `booting_time` was already registered
 * in that gate's `DYNAMIC_PROPERTY_SITES` for the phase timings, so nothing fails; this
 * list is what replaces the reach it lost. Adding a property here means adding a row to
 * `datasrc/mixpanel-lexicon-event-properties.csv` as well.
 */
export const BOOTING_TIME_FIXED_PROPERTY_NAMES = [
  'time_ms',
  'boot_kind',
  'url',
  'settings',
  'warm_resume',
  'prior_session_hint',
  'nav_restore_recovered',
] as const;

/**
 * The whole `booting_time` property bag, for either boot path.
 */
export function buildBootingTimeProperties(inputs: BootingTimeInputs): { [key: string]: any } {
  const {
    bootingTimeMs,
    bootKind,
    warmResume,
    uid,
    bootStartSessionHint,
    settingsForTelemetry,
    pageUrl,
    navRestoreRecovered = false,
    now = DateTime.utc(),
  } = inputs;

  return {
    time_ms: bootingTimeMs,
    boot_kind: bootKind,
    // scheme, host and path only: the community-login return trip boots us on our
    // own page with a working forum login in the address bar (see safe-url-for-log)
    url: safeUrlForLog(pageUrl),
    settings: settingsForTelemetry,
    warm_resume: warmResume,
    // Step 21-prep (#4027): did a still-valid couch session exist at boot start?
    // Classified from the boot-start sample so this boot's own login cannot
    // contaminate the answer — see design-docs/telemetry.md.
    prior_session_hint: classifyPriorSessionHint(bootStartSessionHint, uid, now),
    // df-d80a99934799: true when this boot only finished because the watchdog rescued a wedged
    // page restore, so those boots can be told apart from ones whose restore completed
    nav_restore_recovered: navRestoreRecovered,
    // O1 (#3986): flat per-phase durations (phase_<name>_ms) for every completed
    // boot phase. The warm path fills only the phases it runs (nav restore is still
    // in flight there by design, so navInit/firstHomeRender are absent).
    // See design-docs/telemetry.md
    ...bootTimings.snapshot(),
  };
}
